import {
  fetchDamageExceptionsForExport,
  useDamageExceptionFilters,
  useDamageExceptions,
} from '@/api/DamageExceptions';
import { useTranslate } from '@/translations';
import { AppSearch, DamageExceptionModel } from '@/types';
import { CustomColumns, CustomTable } from '@/ui/components';
import { StorageKeys } from '@/utils/constants';
import { timeConverter } from '@/utils/helpers';
import React, { useCallback, useMemo, useState } from 'react';
import { DamageExceptionDetail } from '../DamageExceptionDetail';

export const DamageExceptionDataGrid = () => {
  const { t } = useTranslate();

  const defaultAppSearch: AppSearch<DamageExceptionModel> = useMemo(
    () => ({
      name: undefined,
      page: 1,
      pageSize: 10,
      filters: [],
      sortField: 'createdAt',
      sortOrder: 'descend',
    }),
    [],
  );
  const [query, setQuery] = useState<
    AppSearch<DamageExceptionModel> | undefined
  >(undefined);

  const { data, isFetching } = useDamageExceptions(query);
  const { data: filterOptions, isFetching: filtersLoading } =
    useDamageExceptionFilters();

  const getExportData = async () => {
    if (!query) return [];
    return fetchDamageExceptionsForExport(query);
  };

  const columns: CustomColumns<DamageExceptionModel> = useMemo(
    () => [
      {
        dataIndex: 'vin',
        title: t('vin'),
        render: (_, record) => (
          <DamageExceptionDetail
            damageExceptionId={record.id}
            vin={record.vin}
          />
        ),
        exportRender: (_, record) => record.vin,
        sortable: true,
        filter: true,
        width: 150,
        fixed: 'left',
      },
      {
        dataIndex: 'type',
        title: 'Type',
        renderType: 'select',
        render: (_, data) => data.typeDescription,
        exportRender: (_, record) => record.typeDescription ?? '',
        sortable: true,
        filter: true,
        filterOptions: filterOptions?.types || [],
        width: 100,
      },
      {
        dataIndex: 'customerName',
        title: 'Customer',
        renderType: 'select',
        filter: true,
        filterOptions: filterOptions?.customers || [],
        width: 120,
        align: 'center',
      },
      {
        dataIndex: 'subCustomerName',
        title: 'SubCustomer',
        renderType: 'select',
        filter: true,
        filterOptions: filterOptions?.subCustomers || [],
        width: 120,
        align: 'center',
      },
      {
        dataIndex: 'location',
        title: 'Location',
        renderType: 'select',
        filter: true,
        filterOptions: filterOptions?.locations || [],
        width: 120,
        align: 'center',
      },
      {
        dataIndex: 'status',
        title: 'Status',
        renderType: 'select',
        render: (_, data) => data.statusDescription,
        exportRender: (_, record) => record.statusDescription ?? '',
        filter: true,
        filterOptions: filterOptions?.statuses || [],
        width: 90,
        align: 'center',
      },
      {
        dataIndex: 'createdAt',
        title: 'Created At',
        renderType: 'date',
        render: (value) => timeConverter(value),
        exportRender: (value) => timeConverter(value) || '',
        sortable: true,
        filter: true,
        width: 120,
        align: 'center',
      },
      {
        dataIndex: 'createdBy',
        title: 'Created By',
        sortable: true,
        filter: true,
        width: 150,
        align: 'center',
      },
    ],
    [filterOptions, t],
  );

  const onAppSearchChange = useCallback(
    (search: AppSearch<DamageExceptionModel>) => setQuery(search),
    [],
  );

  const appSearchOptions = useMemo(
    () => ({
      storageKey: StorageKeys.SearchDamageExceptions,
      defaultSearch: defaultAppSearch,
      value: query,
      onChange: onAppSearchChange,
    }),
    [defaultAppSearch, onAppSearchChange, query],
  );

  return (
    <CustomTable
      dataSource={data?.entities || []}
      columns={columns}
      rowKey="id"
      loading={isFetching || filtersLoading}
      pagination={{
        total: data?.totalCount || 0,
        pageSize: query?.pageSize,
        current: query?.page || 1,
      }}
      exportOptions={{ fileName: 'Damage Exceptions', getExportData }}
      appSearchOptions={appSearchOptions}
      scroll={{ x: '100%' }}
    />
  );
};
